/**
 * NREL Wind Toolkit API — wind resource data.
 * Free API key required (same key as NREL Solar).
 *
 * The provider tries NREL Wind Toolkit v2 first; if it 4xx/5xx or throws,
 * falls back to Open-Meteo's historical hourly wind (free, no key). Both
 * paths emit `[provider:nrel:wind]` telemetry breadcrumbs bucketed by
 * outcome so Cloud Run logs can grep + diagnose silent unavailability.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout } from './types';

export interface WindData {
  windSpeed100m: number;   // m/s — annual average at 100m hub height
  windSpeed80m: number;    // m/s — annual average at 80m
  capacityFactor: number;  // 0-1 estimated capacity factor
}

/**
 * Bucketed outcome of an NREL Wind / Open-Meteo lookup.
 *
 *   - 'ok-nrel'         — NREL Wind Toolkit v2 returned a finite windspeed
 *   - 'ok-openmeteo'    — NREL failed but Open-Meteo fallback succeeded
 *   - 'http-error'      — non-200 from upstream
 *   - 'empty-outputs'   — HTTP 200 but the expected field is missing
 *   - 'parse-error'     — JSON parse failed
 *   - 'network-error'   — fetch threw / aborted
 *   - 'all-failed'      — both upstreams exhausted; provider throws
 */
type NrelWindOutcomeBucket =
  | 'ok-nrel'
  | 'ok-openmeteo'
  | 'http-error'
  | 'empty-outputs'
  | 'parse-error'
  | 'network-error'
  | 'all-failed';

function logWindTelemetry(
  phase: 'request' | 'response',
  payload: Record<string, unknown>,
): void {
  // eslint-disable-next-line no-console
  console.info(`[provider:nrel:wind] ${phase}`, payload);
}

export const nrelWindProvider: DataProvider<WindData> = {
  id: 'nrel-wind',
  name: 'NREL Wind Toolkit',
  cacheTtlMs: CACHE_TTL.CLIMATE_NORMALS,
  requiresKey: false, // Key injected server-side by proxy

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<WindData>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`nrel-wind` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. Wind resource varies slowly relative to the WIND-Toolkit 2 km
    // grid — the centroid usually lands in the same cell as the address — so
    // this is a coordinate-consistency change; the parcel centre is the right
    // point. Both the NREL POINT() query and the Open-Meteo fallback below
    // use it.
    // Try NREL Wind Toolkit first, fall back to Open-Meteo (free, no key)
    let ws100: number | null = null;
    let source = '';
    /** PRR-4 — propagated when the NREL upstream signals auth failure
     *  AND no fallback succeeds; lets the orchestrator's classifier
     *  bucket the failure as `auth-missing` / `auth-invalid` instead of
     *  the generic `all sources failed` → transient-failure. */
    let nrelAuthError: Error | null = null;

    // Attempt 1: NREL Wind Toolkit v2 (api_key injected by server proxy)
    const nrelUrl = `https://developer.nrel.gov/api/wind-toolkit/v2/wind/wtk-download.json?wkt=POINT(${lng} ${lat})&names=2014&attributes=windspeed_100m&interval=60&email=deepatomic@example.com`;
    logWindTelemetry('request', {
      caller: 'nrelWindProvider.fetch.attempt-nrel',
      lat,
      lng,
      upstream: 'developer.nrel.gov/api/wind-toolkit/v2/wind/wtk-download.json',
    });
    try {
      const res = await fetchWithTimeout(nrelUrl, 30_000, geo?.signal);
      if (res.ok) {
        let json: { outputs?: { windspeed_100m?: number; windspeed?: number } } = {};
        try {
          json = await res.json();
        } catch (e) {
          logWindTelemetry('response', {
            caller: 'nrelWindProvider.fetch.attempt-nrel',
            bucket: 'parse-error' as NrelWindOutcomeBucket,
            error: (e as Error)?.message ?? 'unknown',
          });
          json = {};
        }
        const candidate = json.outputs?.windspeed_100m ?? json.outputs?.windspeed ?? null;
        if (candidate != null && Number.isFinite(candidate)) {
          ws100 = candidate;
          source = 'NREL Wind Toolkit v2';
          logWindTelemetry('response', {
            caller: 'nrelWindProvider.fetch.attempt-nrel',
            bucket: 'ok-nrel' as NrelWindOutcomeBucket,
            windspeed100m: ws100,
          });
        } else {
          logWindTelemetry('response', {
            caller: 'nrelWindProvider.fetch.attempt-nrel',
            bucket: 'empty-outputs' as NrelWindOutcomeBucket,
            rawShape: Object.keys(json.outputs ?? {}),
          });
        }
      } else {
        // PRR-4 — capture an `auth-missing` / `auth-invalid` signal so
        // if Open-Meteo also fails the throw at the bottom carries the
        // specific cause rather than the generic `all sources failed`.
        // We don't throw immediately because Open-Meteo may still cover
        // this site keylessly.
        const keySource = res.headers.get('X-DA-Key-Source') ?? '';
        const isAuth = res.status === 401 || res.status === 403;
        if (isAuth && keySource === 'none') {
          nrelAuthError = new Error('NREL Wind: API key not configured (NREL_API_KEY)');
        } else if (isAuth) {
          nrelAuthError = new Error(`NREL Wind: API key rejected (${res.status})`);
        }
        logWindTelemetry('response', {
          caller: 'nrelWindProvider.fetch.attempt-nrel',
          bucket: 'http-error' as NrelWindOutcomeBucket,
          httpStatus: res.status,
          keySource: keySource || null,
        });
      }
    } catch (e) {
      logWindTelemetry('response', {
        caller: 'nrelWindProvider.fetch.attempt-nrel',
        bucket: 'network-error' as NrelWindOutcomeBucket,
        error: (e as Error)?.message ?? 'unknown',
      });
      /* try fallback */
    }

    // Attempt 2: Open-Meteo historical wind (free, no key, always available)
    if (ws100 == null) {
      // Use daily MEAN wind speed (not max) for accurate annual average
      const omUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&start_date=2023-01-01&end_date=2023-12-31&hourly=wind_speed_10m&timezone=auto`;
      logWindTelemetry('request', {
        caller: 'nrelWindProvider.fetch.attempt-openmeteo',
        lat,
        lng,
        upstream: 'archive-api.open-meteo.com',
      });
      try {
        const res = await fetchWithTimeout(omUrl, 20_000, geo?.signal);
        if (res.ok) {
          let json: { hourly?: { wind_speed_10m?: Array<number | null> } } = {};
          try {
            json = await res.json();
          } catch (e) {
            logWindTelemetry('response', {
              caller: 'nrelWindProvider.fetch.attempt-openmeteo',
              bucket: 'parse-error' as NrelWindOutcomeBucket,
              error: (e as Error)?.message ?? 'unknown',
            });
            json = {};
          }
          const hourlyWind = json.hourly?.wind_speed_10m ?? [];
          if (hourlyWind.length > 0) {
            // Open-Meteo hourly wind at 10m in km/h — compute true annual mean
            const validValues = hourlyWind.filter((v: number | null) => v != null && v >= 0) as number[];
            if (validValues.length > 0) {
              const meanKmh = validValues.reduce((s: number, v: number) => s + v, 0) / validValues.length;
              const mean10mMs = meanKmh / 3.6; // km/h → m/s
              // Power law extrapolation: v(100m) = v(10m) × (100/10)^α, α ≈ 0.2 for open terrain
              ws100 = mean10mMs * Math.pow(10, 0.2);
              source = 'Open-Meteo Historical Wind (hourly mean, extrapolated 10m→100m)';
              logWindTelemetry('response', {
                caller: 'nrelWindProvider.fetch.attempt-openmeteo',
                bucket: 'ok-openmeteo' as NrelWindOutcomeBucket,
                windspeed100m: ws100,
                samples: validValues.length,
              });
            } else {
              logWindTelemetry('response', {
                caller: 'nrelWindProvider.fetch.attempt-openmeteo',
                bucket: 'empty-outputs' as NrelWindOutcomeBucket,
                reason: 'no-valid-samples',
              });
            }
          } else {
            logWindTelemetry('response', {
              caller: 'nrelWindProvider.fetch.attempt-openmeteo',
              bucket: 'empty-outputs' as NrelWindOutcomeBucket,
              reason: 'hourly-array-empty',
            });
          }
        } else {
          logWindTelemetry('response', {
            caller: 'nrelWindProvider.fetch.attempt-openmeteo',
            bucket: 'http-error' as NrelWindOutcomeBucket,
            httpStatus: res.status,
          });
        }
      } catch (e) {
        logWindTelemetry('response', {
          caller: 'nrelWindProvider.fetch.attempt-openmeteo',
          bucket: 'network-error' as NrelWindOutcomeBucket,
          error: (e as Error)?.message ?? 'unknown',
        });
        /* exhausted */
      }
    }

    if (ws100 == null || !isFinite(ws100)) {
      logWindTelemetry('response', {
        caller: 'nrelWindProvider.fetch',
        bucket: 'all-failed' as NrelWindOutcomeBucket,
        nrelAuth: nrelAuthError?.message ?? null,
      });
      // PRR-4 — if NREL flagged auth-missing/invalid AND Open-Meteo
      // didn't recover, propagate the specific auth signal so the gap
      // recommendation can name `NREL_API_KEY` directly. Otherwise stay
      // on the legacy `all sources failed` → transient-failure tier.
      if (nrelAuthError) throw nrelAuthError;
      throw new Error('Wind data: all sources failed');
    }

    const ws80 = ws100 * 0.9; // Power law approximation
    // Simple capacity factor estimate: CF ≈ 0 below 3 m/s, linear to ~0.5 at 12 m/s
    const cf = Math.min(0.55, Math.max(0.05, (ws100 - 3) * 0.06));

    return {
      data: { windSpeed100m: parseFloat(ws100.toFixed(1)), windSpeed80m: parseFloat(ws80.toFixed(1)), capacityFactor: parseFloat(cf.toFixed(2)) },
      sources: [{
        field: 'wind',
        source: `${source} — avg wind speed ${ws100.toFixed(1)} m/s at 100m hub height`,
        url: source.includes('Open-Meteo') ? 'https://open-meteo.com/' : 'https://developer.nrel.gov/docs/wind/wind-toolkit/',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
